import { Grid } from "@material-ui/core";
import { Logout } from "@mui/icons-material";
import {
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Stack,
    Typography,
} from "@mui/material";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { AppContext } from "../../AppContext";
import ClosedOrders from "../../components/ClosedOrders/ClosedOrders";
import HelmetMeta from "../../components/HelmetMeta";
import LogoutButton from "../../components/LogoutButton";
import SimpleSpeedDialActions from "../../components/SimpleSpeedDialActions";
import TakeawayOrders from "../../components/TakeawayOrders/TakeawayOrders";
import TakeawayOrdersList from "../../components/TakeawayOrders/TakeawayOrdersList";
import cayLogo from "../../images/logo.png";
import { useSiteSettings } from "../../shared/hooks/useFrappeDoctype";
import { PosContext } from "../../shared/PosContext";
import { useStyles } from "../NewTables/styles";
import PosProfileDialog from "../Widgets/PosProfileDialog";
import ReservationDialog from "../Widgets/ReservationDialog";
import WidgetsDialogs from "../Widgets/WidgetsDialogs";
import CustomerBrowserDialog from "./CustomerBrowserDialog";
import PosCashCloseDialog from "../../components/PosCashCloseDialog/PosCashCloseDialog";
import { useSearchPOSOpening } from "../../shared/hooks/usePOSData";
import { useDraftTakeawayOrders } from "../../shared/hooks/useDraftOrdersData";
import { useNavigate } from "react-router-dom";
import {
    getOrderShortName,
    getTakeawayUrlSegment,
} from "../../utils/floorUtils";
import { dateTimeFormat } from "../../utils/dateUtils";
import { isRoleService } from "../../frappeConfig";
import LanguageToggle from "../../components/LanguageToggle/LanguageToggle";
import { useTranslation } from "react-i18next";
import { useQueries } from "@tanstack/react-query";
import axios from "axios";
import { TextField, InputAdornment } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { enqueueSnackbar } from "notistack";
import CallCenterOrdersDrawer from "../../components/CallCenterOrders/CallCenterOrdersDrawer";
import PosProfilePicker from "../../components/PosProfilePicker";

const OrderTabs = () => {
    const { t } = useTranslation();
    const { data: siteSettings, isFetched } = useSiteSettings();
    const { userInfo, workMode } = useContext(AppContext);
    const { setSelectedCustomer } = useContext(PosContext);
    const [closePOSDialgOpen, setClosePOSDialogOpen] = useState(false);
    const { refetch: refetchTodayOpen } = useSearchPOSOpening({ user: userInfo.name });

    const toggleClosePOSDialog = () =>
        setClosePOSDialogOpen(!closePOSDialgOpen);

    useEffect(() => {
        setSelectedCustomer(null);
    }, []);

    useEffect(() => {
        try {
            const key = "pos_work_mode_toast_seen";
            if (sessionStorage.getItem(key) === "1") return;
            sessionStorage.setItem(key, "1");
            enqueueSnackbar(
                workMode === "call_center"
                    ? t("workMode.callCenter")
                    : t("workMode.cash"),
                { variant: "info" }
            );
        } catch {
            // ignore
        }
    }, [workMode, t]);

    const logoUrl = useMemo(() => {
        if (siteSettings) {
            return siteSettings?.docs[0]?.logo || cayLogo;
        } else {
            if (isFetched) {
                return cayLogo;
            }
        }
    }, [siteSettings]);

    const {
        changeSelectedTable,
        customers,
        posEntryDetails,
        refetchTablesData,
        selectedTable,
        takeawayTable,
    } = useContext(PosContext);

    const { posProfile } = useContext(AppContext);

    const classes = useStyles();

    const selectedOrder = useMemo(() => {
        if (selectedTable) {
            return Object.values(selectedTable.orders)[0];
        } else {
            return null;
        }
    }, [selectedTable]);

    const [widgetsDialogsOpen, setWidgetsDialogsOpen] = useState(false);

    const [takeawayOrdersOpen, setTakeawayOrdersOpen] = useState(false);
    const [callCenterOrdersOpen, setCallCenterOrdersOpen] = useState(false);

    const [closedOrdersOpen, setClosedOrdersOpen] = useState(false);
    const toggleClosedOrders = () => setClosedOrdersOpen(!closedOrdersOpen);

    const [posProfileOpen, setPosProfileOpen] = useState(false);
    const togglePosProfile = () => setPosProfileOpen(!posProfileOpen);

    const [customerBrowserOpen, setCustomerBrowserOpen] = useState(false);
    const toggleCustomerBrowser = () => setCustomerBrowserOpen((v) => !v);

    const handleCallCenterOrders = () => setCallCenterOrdersOpen(true);
    const handleCallCenterOrdersClose = () => setCallCenterOrdersOpen(false);

    const navigate = useNavigate();
    const canCreateNewOrder = !isRoleService(userInfo.role_profile_name);
    const {
        data: draftOrders,
        isLoading: draftOrdersLoading,
        refetch: refetchDraftOrders,
    } = useDraftTakeawayOrders({ owner: userInfo?.name, pos_profile: posProfile?.name });
    const [showAllDrafts, setShowAllDrafts] = useState(false);
    const [searchText, setSearchText] = useState("");

    const normalizedSearch = useMemo(
        () => (searchText || "").trim().toLowerCase(),
        [searchText]
    );
    const customerMobileByName = useMemo(() => {
        if (!Array.isArray(customers)) return {};
        return customers.reduce((acc, customer) => {
            if (customer?.name && customer?.mobile_no) {
                acc[customer.name] = customer.mobile_no;
            }
            return acc;
        }, {});
    }, [customers]);

    const getDraftOrderPhone = (order) =>
        order?.mobile_no || customerMobileByName[order?.customer] || "";

    const highlightText = (text, query) => {
        const value = text == null ? "" : String(text);
        const q = query == null ? "" : String(query);
        if (!q.trim()) return value;
        const lower = value.toLowerCase();
        const needle = q.toLowerCase();
        const idx = lower.indexOf(needle);
        if (idx === -1) return value;
        const before = value.slice(0, idx);
        const match = value.slice(idx, idx + needle.length);
        const after = value.slice(idx + needle.length);
        return (
            <>
                {before}
                <mark
                    style={{
                        background: "rgba(225, 73, 84, 0.22)",
                        color: "inherit",
                        padding: "0 2px",
                        borderRadius: 3,
                    }}
                >
                    {match}
                </mark>
                {after}
            </>
        );
    };

    const filteredDraftOrders = useMemo(() => {
        if (!Array.isArray(draftOrders)) return [];
        return draftOrders.filter((o) => {
            if (posProfile?.name && o?.pos_profile && o.pos_profile !== posProfile.name) {
                return false;
            }
            if (!normalizedSearch) return true;
            const hay = [
                o?.name,
                getOrderShortName(o?.name),
                o?.customer,
                o?.amount,
                getDraftOrderPhone(o),
                o?.designated_number,
                o?.address_line1,
                o?.address_line2,
            ]
                .filter(Boolean)
                .join(" ")
                .toLowerCase();
            return hay.includes(normalizedSearch);
        });
    }, [draftOrders, normalizedSearch, customerMobileByName, posProfile?.name]);

    const displayedDraftOrders = useMemo(() => {
        if (!Array.isArray(filteredDraftOrders)) return [];
        return showAllDrafts ? filteredDraftOrders : filteredDraftOrders.slice(0, 8);
    }, [filteredDraftOrders, showAllDrafts]);

    const draftItemsQueries = useQueries({
        queries: displayedDraftOrders.map((o) => ({
            queryKey: ["draftOrderItemsPreview", o.name],
            queryFn: async () => {
                const params = {
                    model: "Table Order",
                    name: o.name,
                    method: "get_items",
                };
                const { data } = await axios.get("method/frappe_helper.api.call", {
                    params,
                });
                return data?.message || [];
            },
            enabled: !!o?.name,
            staleTime: 10 * 1000,
            cacheTime: 60 * 1000,
        })),
    });

    const draftItemsByOrder = useMemo(() => {
        const map = {};
        displayedDraftOrders.forEach((o, idx) => {
            map[o.name] = draftItemsQueries[idx]?.data || null;
        });
        return map;
    }, [displayedDraftOrders, draftItemsQueries]);

    const formatItemsPreview = (items, max = 2) => {
        if (!Array.isArray(items) || items.length === 0) return "";
        const first = items.slice(0, max).map((it) => {
            const name = it.item_name || it.item_code || t("tables.itemFallback");
            const qty = Number(it.qty || 0);
            return `${qty ? `${qty}× ` : ""}${name}`;
        });
        const remaining = items.length - first.length;
        return remaining > 0 ? `${first.join(", ")} +${remaining}` : first.join(", ");
    };

    useEffect(() => {
        if (!posProfile) {
            setPosProfileOpen(true);
        }
    }, [posProfile]);

    useEffect(() => {
        changeSelectedTable(null);
    }, []);

    useEffect(() => {
        refetchTablesData();
    }, []);

    useEffect(() => {
        // Best-effort: refresh drafts when the page mounts (drafts are not in floor data)
        refetchDraftOrders?.();
    }, []);

    const handleInvoiceClosed = () => {
        setWidgetsDialogsOpen(false);
    };

    const handleCloseDialog = () => {
        setWidgetsDialogsOpen(false);
    };

    const handleTakeawayOrders = () => {
        setTakeawayOrdersOpen(true);
        changeSelectedTable(
            getTakeawayUrlSegment(posProfile, takeawayTable)
        );
    };

    const handleClosedOrders = () => {
        toggleClosedOrders();
    };

    const handleTakeawayOrdersClose = () => {
        setTakeawayOrdersOpen(false);
        changeSelectedTable(null);
    };

    const handleClosePOSCash = () => {
        toggleClosePOSDialog();
    };

    return (
        <React.Fragment>
            <HelmetMeta title={t("tables.helmetTitle")} />
            <div className={classes.PageWrap}>
                <div
                    className={classes.NewTablesPage}
                    style={{ display: "flex", flexDirection: "column" }}
                >
                    <div className={classes.Header} id="Pos_Header">
                        <div
                            className="Logo"
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 12,
                            }}
                        >
                            <img src={logoUrl} alt="" />
                            {workMode === "call_center" ? (
                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontWeight: 800,
                                        letterSpacing: 0.5,
                                    }}
                                >
                                    {t("workMode.callCenterHeading")}
                                </Typography>
                            ) : null}
                        </div>

                        <PosProfilePicker />

                        <div className="SettingsIcon">
                            <Stack
                                direction="row"
                                spacing={1}
                                sx={{ mr: 1, alignItems: "center" }}
                            >
                                <LanguageToggle />
                                {canCreateNewOrder ? (
                                    <Button
                                        size="small"
                                        variant="contained"
                                        onClick={() =>
                                            navigate(
                                                `/pos/${getTakeawayUrlSegment(
                                                    posProfile,
                                                    takeawayTable
                                                )}`
                                            )
                                        }
                                        sx={{
                                            height: 34,
                                            px: 2,
                                            borderRadius: 2,
                                            textTransform: "uppercase",
                                            fontWeight: 700,
                                            marginRight: "1rem !important",
                                            fontSize: "10px",
                                            lineHeight: "15px",
                                            backgroundColor: "#e14954",
                                            "&:hover": {
                                                backgroundColor: "#cc3f49",
                                            },
                                        }}
                                    >
                                        {t("tables.newOrder")}
                                    </Button>
                                ) : null}
                                <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={toggleCustomerBrowser}
                                    sx={{
                                        height: 34,
                                        px: 2,
                                        borderRadius: 2,
                                        textTransform: "uppercase",
                                        fontWeight: 700,
                                        fontSize: "10px",
                                        lineHeight: "15px",
                                        borderColor: "#6E7187",
                                        color: "#6E7187",
                                        "&:hover": {
                                            borderColor: "#6E7187",
                                            backgroundColor: "rgba(110,113,135,0.08)",
                                        },
                                    }}
                                >
                                    {t("tables.customers")}
                                </Button>
                            </Stack>
                            <LogoutButton icon={<Logout />} />
                        </div>
                    </div>
                    <div
                        className={classes.Body}
                        style={{ flex: 1, minHeight: 0, overflow: "auto" }}
                    >
                        <div className={classes.Body__TableView}>
                            <Grid
                                container
                                spacing={2}
                                style={{
                                    padding: "1rem",
                                    flex: 1,
                                    height: "100%",
                                    overflow: "auto",
                                }}
                            >
                                <Grid
                                    s
                                    item
                                    xs={12}
                                    style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        flex: 1,
                                        minHeight: 0,
                                    }}
                                >
                                    <div style={{ flex: 1, minHeight: 0 }}>
                                        <Box sx={{ mb: 1.5 }}>
                                            <TextField
                                                value={searchText}
                                                onChange={(e) =>
                                                    setSearchText(e.target.value)
                                                }
                                                size="small"
                                                fullWidth
                                                placeholder={t("tables.searchPlaceholder")}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <SearchIcon fontSize="small" />
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                        </Box>
                                        <Box sx={{ mb: 1.5 }}>
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "space-between",
                                                    mb: 1,
                                                }}
                                            >
                                                <Typography variant="h6">
                                                    {t("tables.draftOrders")}{" "}
                                                    {Array.isArray(filteredDraftOrders)
                                                        ? `(${filteredDraftOrders.length})`
                                                        : ""}
                                                </Typography>
                                                <Stack
                                                    direction="row"
                                                    spacing={1}
                                                    alignItems="center"
                                                >
                                                    {Array.isArray(filteredDraftOrders) &&
                                                    filteredDraftOrders.length > 8 ? (
                                                        <Button
                                                            size="small"
                                                            variant="text"
                                                            onClick={() =>
                                                                setShowAllDrafts(
                                                                    (v) => !v
                                                                )
                                                            }
                                                        >
                                                            {showAllDrafts
                                                                ? t("common.showLess")
                                                                : t("common.showMore")}
                                                        </Button>
                                                    ) : null}
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        onClick={() =>
                                                            refetchDraftOrders?.()
                                                        }
                                                    >
                                                        {t("common.refresh")}
                                                    </Button>
                                                </Stack>
                                            </Box>
                                            {draftOrdersLoading ? (
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        justifyContent: "center",
                                                        py: 2,
                                                    }}
                                                >
                                                    <CircularProgress size={20} />
                                                </Box>
                                            ) : draftOrders &&
                                              draftOrders.length > 0 ? (
                                                <Box
                                                    sx={{
                                                        display: "grid",
                                                        gridTemplateColumns: {
                                                            xs: "repeat(2, minmax(0, 1fr))",
                                                            sm: "repeat(3, minmax(0, 1fr))",
                                                            md: "repeat(4, minmax(0, 1fr))",
                                                            lg: "repeat(5, minmax(0, 1fr))",
                                                        },
                                                        gap: 1,
                                                    }}
                                                >
                                                    {displayedDraftOrders.map((o) => {
                                                        const draftPhone =
                                                            getDraftOrderPhone(o);
                                                        const designatedTable =
                                                            o?.designated_number;
                                                        return (
                                                        <Card
                                                            key={o.name}
                                                            variant="outlined"
                                                            sx={{ height: "100%" }}
                                                        >
                                                            <CardContent
                                                                sx={{
                                                                    p: 1.25,
                                                                    height: "100%",
                                                                    display: "flex",
                                                                    flexDirection: "column",
                                                                    "&:last-child":
                                                                        {
                                                                            pb: 1.25,
                                                                        },
                                                                }}
                                                            >
                                                                <Typography
                                                                    variant="subtitle2"
                                                                    noWrap
                                                                >
                                                                    {highlightText(
                                                                        getOrderShortName(
                                                                            o.name
                                                                        ),
                                                                        searchText
                                                                    )}
                                                                </Typography>
                                                                <Typography
                                                                    variant="caption"
                                                                    color="text.secondary"
                                                                    noWrap
                                                                    display="block"
                                                                >
                                                                    {highlightText(
                                                                        o.customer || "—",
                                                                        searchText
                                                                    )}
                                                                </Typography>
                                                                {draftPhone ? (
                                                                    <Typography
                                                                        variant="caption"
                                                                        color="text.secondary"
                                                                        noWrap
                                                                        display="block"
                                                                    >
                                                                        {highlightText(
                                                                            draftPhone,
                                                                            searchText
                                                                        )}
                                                                    </Typography>
                                                                ) : null}
                                                                {designatedTable ? (
                                                                    <Typography
                                                                        variant="caption"
                                                                        color="text.secondary"
                                                                        noWrap
                                                                        display="block"
                                                                    >
                                                                        {`Table: ${designatedTable}`}
                                                                    </Typography>
                                                                ) : null}
                                                                <Typography
                                                                    variant="overline"
                                                                    color="text.secondary"
                                                                    display="block"
                                                                    sx={{ mt: 0.5, lineHeight: 1.2 }}
                                                                >
                                                                    {t("tables.amount")}{" "}
                                                                    <span style={{ fontWeight: 700 }}>
                                                                        {o.amount || 0}₪
                                                                    </span>
                                                                </Typography>
                                                                <Typography
                                                                    variant="caption"
                                                                    color="text.secondary"
                                                                    noWrap
                                                                    display="block"
                                                                >
                                                                    {formatItemsPreview(
                                                                        draftItemsByOrder[o.name],
                                                                        2
                                                                    ) ||
                                                                        ""}
                                                                </Typography>
                                                                <Typography
                                                                    variant="caption"
                                                                    color="text.secondary"
                                                                    noWrap
                                                                    display="block"
                                                                    sx={{ mb: 1 }}
                                                                >
                                                                    {dateTimeFormat(
                                                                        o.modified ||
                                                                            o.creation
                                                                    )}
                                                                </Typography>
                                                                <Button
                                                                    size="small"
                                                                    fullWidth
                                                                    variant="contained"
                                                                    sx={{ mt: "auto" }}
                                                                    onClick={() => {
                                                                        navigate(
                                                                            `/pos/${getTakeawayUrlSegment(
                                                                                posProfile,
                                                                                takeawayTable
                                                                            )}/${o.name}`
                                                                        );
                                                                    }}
                                                                >
                                                                    {t("common.open")}
                                                                </Button>
                                                            </CardContent>
                                                        </Card>
                                                        );
                                                    })}
                                                </Box>
                                            ) : (
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                >
                                                    {t("tables.noDraftOrders")}
                                                </Typography>
                                            )}
                                        </Box>
                                        <TakeawayOrdersList
                                            title={t("tables.ordersTitle")}
                                            hideHeaderActions={true}
                                            hideSearchInput={true}
                                            searchText={searchText}
                                            onSearchTextChange={setSearchText}
                                            filterPosProfile={posProfile?.name}
                                        />
                                    </div>
                                </Grid>
                            </Grid>
                        </div>
                        <SimpleSpeedDialActions
                            handleTakeawayOrders={handleTakeawayOrders}
                            handleClosedOrders={handleClosedOrders}
                            handleClosePOSCash={handleClosePOSCash}
                            handleCallCenterOrders={handleCallCenterOrders}
                        />
                    </div>
                </div>
            </div>
            <WidgetsDialogs
                open={widgetsDialogsOpen}
                onClose={handleCloseDialog}
                onInvoiceClose={handleInvoiceClosed}
                order={selectedOrder}
            ></WidgetsDialogs>

            <TakeawayOrders
                open={takeawayOrdersOpen}
                onClose={handleTakeawayOrdersClose}
            />

            <ClosedOrders
                open={closedOrdersOpen}
                onClose={toggleClosedOrders}
            />

            <CallCenterOrdersDrawer
                open={callCenterOrdersOpen}
                onClose={handleCallCenterOrdersClose}
            />

            <PosProfileDialog
                open={posProfileOpen}
                onClose={togglePosProfile}
            />

            <ReservationDialog />

            <CustomerBrowserDialog
                open={customerBrowserOpen}
                onClose={toggleCustomerBrowser}
            />

            {closePOSDialgOpen && (
                <PosCashCloseDialog
                    open={closePOSDialgOpen}
                    onClose={toggleClosePOSDialog}
                    onSuccess={(res) => {
                        refetchTodayOpen();
                    }}
                />
            )}
        </React.Fragment>
    );
};

export default OrderTabs;
